import axios from 'axios'
import APIService from '@/api/services/APIService/APIServiceImpl'
import OrchestratorV1 from '@/api/services/Orchestrator/OrchestratorV1'
import {CheckPaymentStatusResponseDTO, InitPaymentResponse, PaymentPurposeEnum, Promocode, PromocodeConfig, PromocodeProviderEnum, QuoteResponseDTO, UserInfo, ValidatePromocodeRequest, CheckBookingLiveRequestDTO, CheckBookingLiveResponseDTO} from "../../../../../requests-module"
import handleIresaError from "../../func/handleIresaError"
import getCookie from '@/utils/getCookie'
import { isCSE } from '@/utils/cse'
import type { Locale } from '@/service/translate/flights/flightTranslations.types'

const buildQuoteResponseDTO = (data: any) => {
    const response: QuoteResponseDTO = new QuoteResponseDTO(
        data.token,
        data.state,
        data.payments,
        data.userInfo,
        data.booking,
        data.defaultServices,
    )
    response.provideId = data.provideId
    return response
}

export default class OrchestratorV1Impl
    extends APIService
    implements OrchestratorV1 {
    constructor() {
        const serviceUrl: string = OrchestratorV1Impl.getBaseUrl()
        super(serviceUrl)
    }

    private static getBaseUrl(): string {
        const serviceUrl: string | undefined = window.env.VUE_APP_ORCHESTRATOR_BASE_URL
        if (!serviceUrl) throw new Error("VUE_APP_ORCHESTRATOR_BASE_URL cannot be undefined")
        return serviceUrl
    }

    async initPayment(token: string, locale: string,user:UserInfo, provider?: string, agentId?: string): Promise<InitPaymentResponse | undefined> {
        try {
            const params: { [key: string]: string } = {
                ...this.getDefaultParams(),
                locale: locale.replaceAll("-", "_"),
            }
            if (provider) params["provider"] = provider

            if(agentId) {
                params.agentId = agentId
            }


            const headers = {
                ...this.getDefaultHeaders(),
            }
            const url = `${this.getServiceUrl()}/${token}/init-payment`
            const body: UserInfo & { company?: string; companyLang?: string } = { ...user }
            const isCSEContext = isCSE(window.locale as Locale)
            if (window.company && isCSEContext) body.company = window.company
            const companyLang = isCSEContext ? getCookie('companyLang') : undefined
            if (companyLang) body.companyLang = companyLang
            const { data } = await axios.post(url, body, {
                params,
                headers,
            })
            const response: InitPaymentResponse = new InitPaymentResponse(
                data.paymentModes,
                data.token,
                data.payments,
                data.paymentOptions,
                data.paymentCondition,
                data.cancelationDate,
                data.totalPrice
            )
            return response
        } catch (err) {
            await handleIresaError(err, true, true)
        }
    }

    async createQuote(token: string, locale: string): Promise<QuoteResponseDTO | undefined> {
        try {
            const params = {
                ...this.getDefaultParams(),
                locale: locale.replace("-", "_"),
            }
            const headers = {
                ...this.getDefaultHeaders(),
            }
            const url = `${this.getServiceUrl()}/${token}/create-quote`
            console.log("URL IN CREATE: ", url)

            const { data } = await axios.post(url, {}, {
                params,
                headers,
            })
            const response: QuoteResponseDTO = buildQuoteResponseDTO(data)
            return response
        } catch (err) {
            await handleIresaError(err, true, true)
        }
    }

    async updateQuote(token: string, locale: string): Promise<QuoteResponseDTO | undefined> {
        try {
            const params = {
                ...this.getDefaultParams(),
                locale: locale.replace("-", "_"),
            }
            const headers = {
                ...this.getDefaultHeaders(),
            }
            const url = `${this.getServiceUrl()}/${token}/update-quote`
            const { data } = await axios.put(url, {}, {
                params,
                headers,
            })
            const response: QuoteResponseDTO = buildQuoteResponseDTO(data)
            return response
        } catch (err) {
            await handleIresaError(err, true, true)
        }
    }

    async checkPaymentStatus(token: string, locale: string, paymentId: string): Promise<CheckPaymentStatusResponseDTO | undefined> {
        try {
            const params = {
                ...this.getDefaultParams(),
                locale: locale.replace("-", "_"),
            }
            const headers = {
                ...this.getDefaultHeaders(),
            }
            const url = `${this.getServiceUrl()}/${token}/check-payment-status/${paymentId}`
            const res = await axios.post(url, {}, {
                params,
                headers,
            })
            if (!res.data.status) throw new Error("res.data.status must be defined")
            const data = new CheckPaymentStatusResponseDTO(
                res.data.status,
            )
            return data
        } catch (err) {
            await handleIresaError(err, false, false)
        }
    }

    async validatePromocode(
        token: string,
        locale: string,
        promocode: string,
        promocodeProvider: PromocodeProviderEnum,
        formData: { [key: string]: any },
    ): Promise<boolean> {
        let isValid = false
        try {
            const params = {
                ...this.getDefaultParams(),
                locale: locale.replace("-", "_"),
            }
            const headers = {
                ...this.getDefaultHeaders(),
            }
            const url = `${this.getServiceUrl()}/${token}/validate-promocode`
            const body = new ValidatePromocodeRequest(promocode, promocodeProvider, formData)
            const { data } = await axios.post(url, body, {
                params,
                headers,
            })
            isValid = data ? true : false
        } catch (err) {
            await handleIresaError(err, false, false)
        }
        return isValid
    }

    async getPromocodeConfig(token: string, locale: string, promocode: string, liheID: number): Promise<PromocodeConfig | undefined | null> {
        try {
            const params = {
                ...this.getDefaultParams(),
                locale: locale.replace("-", "_"),
            }
            const headers = {
                ...this.getDefaultHeaders(),
            }
            const url = `${this.getServiceUrl()}/${token}/get-promocode-config`
            const body = new Promocode(promocode, liheID)
            const { data } = await axios.post(url, body, {
                params,
                headers,
            })
            if (Object.keys(data).length === 0) return null
            const config: PromocodeConfig = new PromocodeConfig(
                data.fields,
                data.provider,
                data.title_content_name,
            )
            return config
        } catch (err) {
            await handleIresaError(err, false, false)
        }
    }

    async getState(token: string, locale: string): Promise<QuoteResponseDTO | undefined | null> {
        try {
            const params = {
                ...this.getDefaultParams(),
                locale: locale.replace("-", "_"),
            }
            const headers = {
                ...this.getDefaultHeaders(),
            }
            const url = `${this.getServiceUrl()}/${token}/get-state`
            const { data } = await axios.get(url, {
                params,
                headers,
            })
            if (Object.keys(data).length === 0) return null
            const response: QuoteResponseDTO = buildQuoteResponseDTO(data)
            return response
        } catch (err) {
            await handleIresaError(err, false, false)
        }
    }

    async checkLive(dto: CheckBookingLiveRequestDTO, locale: string): Promise<CheckBookingLiveResponseDTO> {
        try {
            const headers = {
                ...this.getDefaultHeaders(),
            }

            const params = {
                ...this.getDefaultParams(),
                locale: locale.replace("-", "_"),
            }
            const url = `${this.getServiceUrl()}/booking-live`
            const { data } = await axios.post<CheckBookingLiveResponseDTO>(url, dto, {headers, params})
            return data
        } catch (err) {
            await handleIresaError(err)
            throw err
        }
    }


    public getDefaultParams(): {
        [key: string]: string;
    } {
        const locale = this.getLocale()
        return { locale, purpose: PaymentPurposeEnum.FIRST_PAYMENT }
    }

}